/* eslint-disable react-refresh/only-export-components */
import React, { useCallback, useEffect, useLayoutEffect, useState, useSyncExternalStore } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { userService } from "../api/services/user.service";
import { authService } from "../api/services/auth.service";
import { MailWarning, X } from "lucide-react";

/* ------------------------------------------------------------ resend logic */

const COOLDOWN_SECONDS = 60;
/** Shared across every resend control so moving between pages doesn't reset the cooldown */
let cooldownUntil = 0;
const secondsLeft = () => Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));

/**
 * Resend-verification-email action with a 60s cooldown and a short "sent" state.
 * Used by the global banner and by the dashboard setup card's verify step.
 */
export const useResendVerificationEmail = () => {
  const [cooldownSeconds, setCooldownSeconds] = useState(secondsLeft);
  const [showSuccess, setShowSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: authService.resendVerificationEmail,
    onSuccess: () => {
      cooldownUntil = Date.now() + COOLDOWN_SECONDS * 1000;
      setShowSuccess(true);
      setCooldownSeconds(COOLDOWN_SECONDS);
    },
    onError: (error: unknown) => {
      console.error("Failed to resend verification email:", error);
    },
  });

  // Hide the success message after 5 seconds
  useEffect(() => {
    if (!showSuccess) return;
    const timer = setTimeout(() => setShowSuccess(false), 5000);
    return () => clearTimeout(timer);
  }, [showSuccess]);

  // Cooldown timer
  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const timer = setTimeout(() => setCooldownSeconds(secondsLeft()), 1000);
    return () => clearTimeout(timer);
  }, [cooldownSeconds]);

  const { isPending, isError, mutate } = mutation;
  const resend = useCallback(() => {
    if (cooldownSeconds === 0 && !isPending) mutate();
  }, [cooldownSeconds, isPending, mutate]);

  return {
    resend,
    isPending,
    isError,
    showSuccess,
    cooldownSeconds,
    disabled: cooldownSeconds > 0 || isPending || showSuccess,
    label: isPending ? "Sending…" : cooldownSeconds > 0 ? `Resend in ${cooldownSeconds}s` : "Resend email",
  };
};

/* ----------------------------------------------- banner suppression store */

// While a page shows its own verify-email step (the dashboard setup card), the banner
// steps aside so the same fact isn't announced twice.
let suppressors = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const isSuppressed = () => suppressors > 0;

/** Hides the global EmailVerificationBanner while `active` and the caller is mounted */
export const useSuppressEmailVerificationBanner = (active: boolean) => {
  useLayoutEffect(() => {
    if (!active) return;
    suppressors += 1;
    emit();
    return () => {
      suppressors -= 1;
      emit();
    };
  }, [active]);
};

/* ------------------------------------------------------------------ banner */

const EmailVerificationBanner: React.FC = () => {
  const [isDismissed, setIsDismissed] = useState(false);
  const suppressed = useSyncExternalStore(subscribe, isSuppressed, () => false);
  const { resend, isError, showSuccess, disabled, label } = useResendVerificationEmail();

  // Fetch user profile to check email verification status
  const { data: profile } = useQuery({
    queryKey: ["userProfile"],
    queryFn: userService.getProfile,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Reset dismissed state when profile changes
  useEffect(() => {
    setIsDismissed(false);
  }, [profile?.emailVerified]);

  // Don't show banner if:
  // - Profile not loaded yet
  // - Email is already verified
  // - Banner is dismissed
  // - The page shows its own verify-email step
  if (!profile || profile.emailVerified || isDismissed || suppressed) {
    return null;
  }

  return (
    <div className="border-b border-amber-200/70 bg-amber-50">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 sm:px-6">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <span className="flex shrink-0 text-amber-700">
            <MailWarning className="size-4" />
          </span>
          <p className="text-sm text-amber-900">
            <span className="font-medium">Verify your email</span>
            <span className="hidden text-amber-800/80 sm:inline"> to unlock bidding, full addresses and alerts.</span>
          </p>
        </div>

        <div className="flex items-center gap-3">
          {showSuccess && <span className="text-sm font-medium text-emerald-700">Sent. Check your inbox.</span>}
          {isError && <span className="text-sm font-medium text-red-700">Couldn't send. Try again.</span>}
          <button
            type="button"
            onClick={resend}
            disabled={disabled}
            className="h-7 rounded-md bg-white px-2.5 text-meta font-medium text-amber-900 shadow-xs ring-1 ring-amber-300 transition-colors hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {label}
          </button>
          <button
            type="button"
            onClick={() => setIsDismissed(true)}
            className="rounded-md p-1 text-amber-700 transition-colors hover:bg-amber-100"
            aria-label="Dismiss banner"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
